/** QuickTicketing — Administration : administrateurs, clés d'API (Claude), applications suivies. */
import React, { useEffect, useState } from 'react';
import { api, fmtDate } from '../api/client.js';

/** Même règle que le serveur (server/settings.js) : « Quick Vision » → « quick-vision ». */
const slugId = (s) => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32);

export default function Admin() {
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { api('/admin/settings').then(setS).catch((e) => setError(e.message)); }, []);
  if (error) return <div className="page narrow"><div className="error-line">{error}</div></div>;
  if (!s) return <div className="page narrow empty"><span className="spinner" /> Chargement…</div>;
  return (
    <div className="page narrow">
      <h2>Administration</h2>
      <Admins s={s} setS={setS} />
      <Keys s={s} setS={setS} />
      <Apps s={s} setS={setS} />
    </div>
  );
}

function Admins({ s, setS }) {
  const [text, setText] = useState(s.admins.join(', '));
  const [msg, setMsg] = useState(null);
  const save = async () => {
    try {
      const r = await api('/admin/admins', { method: 'PUT', body: { admins: text.split(/[,\s]+/).filter(Boolean) } });
      setS({ ...s, admins: r.admins });
      setText(r.admins.join(', '));
      setMsg('Enregistré.');
    } catch (e) { setMsg(e.message); }
  };
  return (
    <section className="card">
      <h3>Administrateurs du ticketing</h3>
      <p className="muted">Ils voient tous les tickets, les qualifient (à analyser, à redévelopper…), fixent la priorité et écrivent des notes internes. Les administrateurs du portail le sont d'office.</p>
      {s.canManageAdmins ? (
        <div className="row">
          <input className="grow" value={text} onChange={(e) => setText(e.target.value)} placeholder="identifiants du portail, séparés par des virgules" />
          <button className="primary" onClick={save}>Enregistrer</button>
        </div>
      ) : (
        <p>{s.admins.length ? s.admins.join(', ') : <span className="muted">Aucun (hors administrateurs du portail).</span>} <span className="muted">— modifiable par un administrateur du portail.</span></p>
      )}
      {msg && <div className="muted">{msg}</div>}
    </section>
  );
}

function Keys({ s, setS }) {
  const [label, setLabel] = useState('claude');
  const [fresh, setFresh] = useState(null);
  const [error, setError] = useState(null);
  const create = async () => {
    setError(null);
    try {
      const r = await api('/admin/keys', { method: 'POST', body: { label } });
      setFresh(r.key);
      setS({ ...s, keys: [...s.keys, r.info] });
    } catch (e) { setError(e.message); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Révoquer la clé « ${k.label} » (${k.prefix}…) ? Ce qui l'utilise perdra l'accès.`)) return;
    try {
      await api(`/admin/keys/${k.id}`, { method: 'DELETE' });
      setS({ ...s, keys: s.keys.filter((x) => x.id !== k.id) });
    } catch (e) { setError(e.message); }
  };
  const origin = window.location.origin;
  const cmd = fresh && `claude mcp add --scope user --transport http quickticketing ${origin}/mcp --header "Authorization: Bearer ${fresh}"`;
  return (
    <section className="card">
      <h3>Clés d'API — traitement par Claude</h3>
      <p className="muted">Une clé donne à Claude Code l'accès au serveur MCP <code>{origin}/mcp</code> (outils <code>tk_list</code>, <code>tk_get</code>, <code>tk_comment</code>, <code>tk_set_status</code>, <code>tk_resolve</code>…) avec les droits d'un administrateur. Le nom de la clé signe ses interventions dans le fil.</p>
      {s.keys.length > 0 && (
        <table className="tickets compact">
          <thead><tr><th>Nom</th><th>Clé</th><th>Créée</th><th>Dernier usage</th><th /></tr></thead>
          <tbody>
            {s.keys.map((k) => (
              <tr key={k.id}>
                <td>{k.label}</td>
                <td className="mono">{k.prefix}…</td>
                <td className="muted">{fmtDate(k.createdAt)} par {k.createdBy}</td>
                <td className="muted">{k.lastUsedAt ? fmtDate(k.lastUsedAt) : 'jamais'}</td>
                <td><button className="danger" onClick={() => revoke(k)}>Révoquer</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="nom (ex. claude)" maxLength={40} />
        <button className="primary" onClick={create}>Créer une clé</button>
      </div>
      {error && <div className="error-line">{error}</div>}
      {fresh && (
        <div className="notice">
          <b>Copiez cette clé maintenant : elle ne sera plus affichée.</b>
          <pre className="code-block selectable">{fresh}</pre>
          Déclaration dans Claude Code :
          <pre className="code-block selectable">{cmd}</pre>
          <button onClick={() => navigator.clipboard?.writeText(cmd)}>Copier la commande</button>
        </div>
      )}
    </section>
  );
}

function Apps({ s, setS }) {
  const [rows, setRows] = useState(s.apps);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  // Une nouvelle ligne déduit son id du nom tant que l'id n'a pas été saisi à la main.
  const set = (i, k) => (e) => setRows((r) => r.map((a, j) => {
    if (j !== i) return a;
    const next = { ...a, [k]: e.target.value };
    if (k === 'name' && a.isNew && !a.idTouched) next.id = slugId(e.target.value);
    if (k === 'id') next.idTouched = true;
    return next;
  }));
  const save = async () => {
    setMsg(null);
    setError(null);
    try {
      const r = await api('/admin/apps', { method: 'PUT', body: { apps: rows.map(({ id, name, repo, path }) => ({ id, name, repo, path })) } });
      setS({ ...s, apps: r.apps });
      setRows(r.apps);
      setMsg('Enregistré — rechargez la page pour mettre à jour les listes.');
    } catch (e) { setError(e.message); }
  };
  return (
    <section className="card">
      <h3>Applications suivies</h3>
      <p className="muted">Proposées au demandeur. Le dépôt et le clone local sont transmis à Claude avec chaque ticket, pour qu'il sache où corriger.</p>
      <table className="tickets compact apps-table">
        <thead><tr><th>Id</th><th>Nom</th><th>Dépôt Git</th><th>Clone local</th><th /></tr></thead>
        <tbody>
          {rows.map((a, i) => (
            <tr key={i}>
              <td><input value={a.id} onChange={set(i, 'id')} size={9} placeholder="auto" /></td>
              <td><input value={a.name} onChange={set(i, 'name')} placeholder="nom affiché" /></td>
              <td><input value={a.repo} onChange={set(i, 'repo')} className="wide" /></td>
              <td><input value={a.path} onChange={set(i, 'path')} /></td>
              <td><button className="icon" title="Retirer" onClick={() => setRows((r) => r.filter((_, j) => j !== i))}>✕</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row">
        <button onClick={() => setRows((r) => [...r, { id: '', name: '', repo: '', path: '', isNew: true }])}>+ Application</button>
        <span className="grow" />
        {msg && <span className="muted">{msg}</span>}
        <button className="primary" onClick={save}>Enregistrer</button>
      </div>
      {error && <div className="error-line">{error}</div>}
    </section>
  );
}
